import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { FieldChange, PrimaryEntityType } from '@/lib/types';
import type { ReciprocalDeltaPayload } from '@/lib/utils/generate-reciprocal-deltas';
import { EntitySummary } from '@common/schemas/entities/summary.schema';
import { EventChangeCard, type EventEntityChangeWithFailure } from './event-change-card';

interface EventImplicationsSectionProps {
  eventId: string;
  eventData: {
    name?: string;
    order?: number;
    characterIds?: string[];
    organizationIds?: string[];
    locationIds?: string[];
    eventChanges?: EventEntityChangeWithFailure[];
  };
  onSaveOverride?: (entityId: string, entityType: PrimaryEntityType, entityName: string, changes: FieldChange[], reciprocalChanges: ReciprocalDeltaPayload[], description: string) => Promise<void>;
  readOnly?: boolean;
}

export function EventImplicationsSection({
  eventId,
  eventData,
  onSaveOverride,
  readOnly,
}: EventImplicationsSectionProps) {
  const { t } = useTranslation();
  const { getSummaries } = useNovelRepository();

  const [expandedEntityId, setExpandedEntityId] = useState<string | null>(null);

  const eventChanges: EventEntityChangeWithFailure[] = eventData.eventChanges || [];

  // Group involved entity IDs
  const involvedEntities = useMemo(() => {
    const chars = getSummaries<EntitySummary>('characters');
    const locs = getSummaries<EntitySummary>('locations');
    const orgs = getSummaries<EntitySummary>('organizations');

    const result: Array<{ id: string, name: string, type: PrimaryEntityType }> = [];

    (eventData.characterIds || []).forEach(id => {
      result.push({ id, name: chars.find(c => c.id === id)?.name || id, type: 'characters' });
    });
    (eventData.locationIds || []).forEach(id => {
      result.push({ id, name: locs.find(c => c.id === id)?.name || id, type: 'locations' });
    });
    (eventData.organizationIds || []).forEach(id => {
      result.push({ id, name: orgs.find(c => c.id === id)?.name || id, type: 'organizations' });
    });

    return result;
  }, [eventData, getSummaries]);

  const hasInvolvedEntities = involvedEntities.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('event_implications.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('event_implications.description')}
              </Typography>
            </Box>
          </Stack>

          {/* No involved entities warning */}
          {!hasInvolvedEntities && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'warning.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <Typography variant="body2" color="warning.dark">
                {t('event_implications.involved_parties_warning')}
              </Typography>
            </Box>
          )}

          {/* Grid of Event Change Cards */}
          {hasInvolvedEntities && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {involvedEntities.map((entity) => {
                const existingChange = eventChanges.find(c => c.entityId === entity.id);
                const isExpanded = expandedEntityId === entity.id;
                return (
                  <Grid
                    key={entity.id}
                    size={{
                      xs: isExpanded ? 12 : 6,
                      sm: isExpanded ? 12 : 4,
                      md: isExpanded ? 12 : 3,
                      lg: isExpanded ? 12 : 2
                    }}
                  >
                    <EventChangeCard
                      eventId={eventId}
                      eventName={eventData.name || ''}
                      eventOrder={eventData.order || 0}
                      entityId={entity.id}
                      entityName={entity.name}
                      entityType={entity.type}
                      existingChange={existingChange}
                      expanded={isExpanded}
                      onToggle={() => setExpandedEntityId(isExpanded ? null : entity.id)}
                      onSaveOverride={onSaveOverride}
                      readOnly={readOnly}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
